import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import { useInfiniteOrganizationRoster } from '@/hooks/organization/organization';
import { Center, HStack, Input, InputGroup, InputLeftElement, Text, VStack } from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import UsersTable from '../user/UsersTable';
import TabButtons from '@/components/atoms/TabButtons';
import SvgIcon from '@/components/atoms/SvgIcon';
import { svgIconPaths } from '@/assets/svg/icons';
import colors from '@/theme/colors';
import { useTableColumnSort } from '@/hooks/useTableColumnSort';
import FilterSelectPopover from '@/components/molecules/FilterSelectPopover';
import { TUserActivationToggle } from '@/types/User.types';
import CustomButton from '@/components/atoms/CustomButton';
import RosterActionsPopover from '../user/active/RosterActionsPopover';
import { isUndefined } from 'lodash';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import { useUserActivation } from '@/hooks/user/users';

type Props = {
  organizationId: string;
  disabled?: boolean;
  disabledExplanation?: string;
};

export type TAdminOrganizationRosterSortingColumn = 'First Name' | 'Last Name' | 'Email' | 'Last Sign In';
const SEARCH_TEXT_INIT = '';

const OrganizationRoster = ({ organizationId, disabled, disabledExplanation }: Props) => {
  const [activeUsers, setActiveUsers] = useState<'activated' | 'deactivated'>('activated');

  const isActiveUsers = activeUsers === 'activated';

  const { sortingColumn, sortingDirection } = useTableColumnSort<TAdminOrganizationRosterSortingColumn>({
    defaultSortingColumn: 'First Name',
  });

  const [localSearchText, setLocalSearchText] = useState(SEARCH_TEXT_INIT);
  const [committedSearchText, setCommittedSearchText] = useState(SEARCH_TEXT_INIT);
  const searchIsActive = committedSearchText.length > 0;
  const searchSubmitButtonDisabled = localSearchText.length < MIN_CHARS_TO_DO_SEARCH;

  const { isLoading, isRefetching, data, refetch, infinitePagination } = useInfiniteOrganizationRoster({
    id: organizationId,
    activeUsers: activeUsers === 'activated',
    sortingColumn,
    sortingDirection,
    searchText: committedSearchText,
  });

  const submitLocalSearchForm = () => {
    setCommittedSearchText(localSearchText);

    refetch(); // was pagination.getFirstPage();
  };

  const resetSearchForm = () => {
    setLocalSearchText(SEARCH_TEXT_INIT);
    setCommittedSearchText(SEARCH_TEXT_INIT);

    refetch();
  };

  const onToggleActiveUsers = (activeUsers: 'activated' | 'deactivated') => {
    setActiveUsers(activeUsers);
    refetch();
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const [isFilterExternallyOpen, setIsFilterExternallyOpen] = useState(false);

  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TUserActivationToggle>();

  const onActivationSuccess = () => {
    setIsActivationModalOpen(false);
    refetch();
  };

  const { mutate: activateUser, isPending } = useUserActivation();

  const activateSelectedUser = () => {
    if (selectedUser) {
      activateUser({ userId: selectedUser.id }, { onSuccess: onActivationSuccess });
    }
  };

  return (
    <VStack width={'100%'}>
      <HStack width={'100%'} justifyContent={'space-between'}>
        <TabButtons
          options={[
            {
              onClick: () => onToggleActiveUsers('activated'),
              labelValue: 'activated',
              labelDisplay: 'Active',
            },
            {
              onClick: () => onToggleActiveUsers('deactivated'),
              labelValue: 'deactivated',
              labelDisplay: 'Deactivated',
            },
          ]}
          selectedOptionValue={activeUsers}
        />
        <FilterSelectPopover
          filterIconPath={svgIconPaths.magnifyingGlass}
          filterIconText="Search"
          contentWidth="260px"
          onFilterSubmit={submitLocalSearchForm}
          onFilterReset={resetSearchForm}
          submitButtonDisabled={searchSubmitButtonDisabled}
          isFilterActive={searchIsActive}
          isExternalOpen={isFilterExternallyOpen}
          setIsExternalOpen={setIsFilterExternallyOpen}
          onFilterOpen={() =>
            setTimeout(() => {
              inputRef.current?.focus();
            }, 0)
          }
          popoverContent={
            <VStack marginBottom={'24px'} alignItems={'start'} width={'100%'}>
              <Text variant={'urbanistBold'} marginTop={'10px'}>
                Search By Email, First and Last Name
              </Text>

              <InputGroup marginTop={'10px'} flexDir={'column'}>
                <InputLeftElement>
                  <Center height={'100%'} marginTop={'18px'}>
                    <SvgIcon
                      iconPath={svgIconPaths.magnifyingGlass}
                      color={localSearchText.length > 0 ? colors.text.darkBlue : colors.text.mediumGray}
                    />
                  </Center>
                </InputLeftElement>
                <Input
                  ref={inputRef}
                  className="custom-input"
                  backgroundColor={'white'}
                  placeholder="Search..."
                  _placeholder={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                  value={localSearchText}
                  onKeyDown={(event) => {
                    if (event.nativeEvent.key === 'Enter') {
                      submitLocalSearchForm();
                      setIsFilterExternallyOpen(false);
                    }
                  }}
                  onChange={(e) => setLocalSearchText(e.target.value)}
                  _disabled={{ opacity: 0.8, cursor: 'not-allowed' }}
                />
                <Text variant={'urbanistSmallBold'} color={'text.mediumGray'} padding={'5px'}>
                  Minimum 3 characters
                </Text>
              </InputGroup>
            </VStack>
          }
        />
      </HStack>
      <UsersTable
        isLoading={isLoading}
        // pagination={pagination}
        infinitePagination={infinitePagination}
        isRefetching={isRefetching}
        users={data}
        isSearching={false}
        showOrganizationColumn={false}
        ActionElement={(userToUpdate: TUserActivationToggle) => {
          const isUserOnboarded = !isUndefined(userToUpdate?.user_metadata?.sex);

          return (
            <>
              {!isActiveUsers && (
                <CustomButton
                  isTransparent
                  label={'Activate'}
                  hoverBackgroundColor={'primary.500'}
                  labelColor={colors.text.mediumBlue}
                  labelHoverColor={colors.extra.white}
                  iconBeforeLabelSvgPath={svgIconPaths.userDeactivate}
                  disabled={disabled}
                  disabledExplanation={disabledExplanation}
                  onClick={() => {
                    setIsActivationModalOpen(true);
                    setSelectedUser(userToUpdate);
                  }}
                  width={'120px'}
                  height={'35px'}
                  noShadow
                />
              )}
              {isActiveUsers && (
                <RosterActionsPopover
                  selectedUser={userToUpdate}
                  isUserOnboarded={isUserOnboarded}
                  disabled={disabled}
                  disabledExplanation={disabledExplanation}
                  refetch={refetch}
                />
              )}
            </>
          );
        }}
      />

      <ConfirmationModal
        isOpen={isActivationModalOpen}
        onCancel={() => {
          setIsActivationModalOpen(false);
          setSelectedUser(undefined);
        }}
        title={isActiveUsers ? 'Confirm Deactivation' : 'Confirm Activation'}
        confirmButtonText={isActiveUsers ? 'Deactivate' : 'Activate'}
        description={`This will ${isActiveUsers ? 'deactivate ' : 'activate'} user ${selectedUser?.firstName} ${selectedUser?.lastName}`}
        onConfirm={activateSelectedUser}
        confirmButtonColor={isActiveUsers ? 'extra.red' : 'primary.500'}
        confirmLoading={isPending}
      />
    </VStack>
  );
};

export default OrganizationRoster;
